import {inject, Injectable } from '@angular/core';
import {PreferenceService, StudioService, UserService, WmHttpService} from "@wms/app/services";
import { APP_OVERRIDE_CSS_PATH, APP_STUDIO_OVERRIDE_CSS_PATH, getProjectPath, getProjectResourcePath, loadStyleSheet } from "@wms/core";
import { Subject } from "rxjs";
import { getClonedObject, replace } from "@wm/core";
import { BaseComponent } from "@wms/components";
import {isEmpty} from "lodash-es";
const semver = require('semver');

@Injectable({
  providedIn: 'root'
})
export class ThemeService extends  BaseComponent {

  private TARGET: string;
  appStudioThemes: any;
  appThemeNamePropertiesMap: any;
  switchTheme$: Subject<any>;
  projectThemes: any;
  appProjectThemes: any
  studioService: StudioService;
  organizationsList: any[];
  userService: UserService;
  user: any;
  selectedOrganization: any;
  preferenceService:PreferenceService;

  constructor(private wmHttpService: WmHttpService) {
    super();
    this.studioService = inject(StudioService);
    this.userService = inject(UserService);
    this.preferenceService = inject(PreferenceService);
    this.switchTheme$ = new Subject();
    this.TARGET = "Theme";
  }
  switchThemeObservable() {
    return this.switchTheme$.asObservable();
  }

  emitSwitchTheme(theme: any) {
    this.switchTheme$.next(theme);
  }


  upload(params: any): Promise<any> {
    let action = 'import'

    if(this.preferenceService.hasAccess("project.themes.import")){
      action = "importToProject";
    }
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: action,
      pathParams: {
        projectID: this.projectService.getProjectId()
      },
      data: params.content
    }) as Promise<any>);
  }




  listInStudio(showSpinner: boolean = false): Promise<any> {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: 'listInStudio',
      pathParams: {
        platformType: this.projectService.getProjectPlatformType()
      },
      spinnerInfo: {
        showSpinner: showSpinner
      }
    }) as Promise<any>);
  }

  remove(params: any) {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: 'remove',
      pathParams: {
        themeID: params.themeID,
        versionId: params.versionId
      }
    }) as Promise<any>);
  }

  removeTheme(params: any) {
    return new Promise((res, rej) => {
      this.remove(params).then(() => {
        this.loadThemes().then(res.bind(this));
      }).catch(() => {
        rej();
      });
    });
  }

  listInProject(): Promise<any> {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: 'listInProject',
      pathParams: {
        projectID: this.projectService.getProjectId()
      }
    }) as Promise<any>);
  }

  register(requestBody: any): Promise<any> {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: 'register',
      pathParams: {
        projectID: this.projectService.getProjectId()
      },
      data: requestBody
    }) as Promise<any>);
  }
  deRegister(theme: string): Promise<any> {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: 'deRegister',
      pathParams: {
        projectID: this.projectService.getProjectId()
      },
      data: theme
    }) as Promise<any>);
  }

  setActiveTheme(theme: string): Promise<any> {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: 'setActive',
      pathParams: {
        projectID: this.projectService.getProjectId()
      },
      data: theme
    }) as Promise<any>);
  }
  getActiveTheme(): Promise<any> {
    return (this.wmHttpService.execute({
      target: this.TARGET,
      action: 'getActive',
      pathParams:{
        projectID: this.projectService.getProjectId()
      },
    })as Promise<any>);
  }

  loadActiveTheme(): void {
    // get active theme from project details
    let theme = this.projectService.getActiveTheme();
    if (this.projectService.isNativeMobileApplicationType()) {
      theme += '/android';
    }
    const themePath = getProjectResourcePath(this.projectService.getProjectId()) + 'themes/' + theme + '/style.css';
    loadStyleSheet(themePath, { name: "theme", value: "wmtheme" });
  }

  loadMobileFoundationTheme() {
    const themePath = getProjectPath(this.projectService.getProjectId()) + "/src/main/webapp/design-tokens/foundation/android/style.css";
    loadStyleSheet(themePath, { name: "theme", value: "wmtheme" });
    this.reloadAppOverrideCSS();
  }

  reloadAppOverrideCSS() :Promise<any>{
    return new Promise((res) => {
        document.querySelectorAll('[tokens="app-override"]').forEach(el => el.remove());
        const themePath = getProjectPath(this.projectService.getProjectId()) + "/src/main/webapp/" + APP_STUDIO_OVERRIDE_CSS_PATH + "?t=" + Date.now();
        const override = loadStyleSheet(themePath, { name: "tokens", value: "app-override" });
        res(override?.sheet?.cssRules?.length);
    });
  }

  /**
   * @ngdoc function
   * @name wm.themes.$ThemeManager#loadThemes
   * @methodOf wm.themes.$ThemeManager
   * @function
   *
   * @description
   * load all the themes
   *
   * @param {boolean} scope controller scope to bind model data with view
   */
  loadThemes(showSpinner = true) {
    return new Promise((res, rej) => {
      this.getThemeList(showSpinner).then((themes) => res(themes)).catch(() => rej());
    });
  }

  /*getThemeList gets list of Themes available for studio*/
  getThemeList(showSpinner: boolean) {
    let themes: any = [];
    return new Promise((res, rej) => {
      this.listInStudio(showSpinner).then((themesList) => {


        //fetch the configuration of the themes in asynchronous way and trigger the callback function
        themesList.forEach((theme: any) => {
          theme.category = 'themes';
          theme.iconUrl = 'services/themes/' + theme.id + '/versions/' + theme.versionId + '/files/theme.png';
          theme.removable = !!theme.owner;
          themes.push(theme);
        });

        this.setStudioThemes(themes);
        res(themes);
      }).catch(() => {
        rej();
      });
    });
  }

  getProjectThemesList() {
    return new Promise((res, rej) => {
      this.listInProject().then((themesList) => {
        this.projectThemes = [];

        //fetch the configuration of the themes in asynchronous way and trigger the callback function
        themesList.forEach((theme: any) => {
          theme.category = 'themes';
          theme.iconUrl = theme.icon.replace(/:projectID/ig, this.projectService.getProjectId());
          theme.removable = !!theme.owner;
          this.projectThemes.push(theme);
        });
        this.setProjectThemes(this.projectThemes);
        res(this.projectThemes)
      });
    });
  }

  setProjectThemes(themes: any) {
    this.appProjectThemes = themes;
  }

  /**
  * this function sets the Studio themes
  * @param themes, themes config array
  */
  setStudioThemes(themes: any) {
    this.appStudioThemes = themes;
    this.prepareAppThemesNamePropertiesMap();
  }

  getStudioTheme(themeName: any) {
    return getClonedObject(this.appThemeNamePropertiesMap[themeName]) || {};
  }

  //prepares the project themes map
  prepareAppThemesNamePropertiesMap() {
    this.appThemeNamePropertiesMap = {};
    this.appStudioThemes.forEach((themeObj: any) => {
      this.appThemeNamePropertiesMap[themeObj.name] = themeObj;
    });
  }

  getProjectThemes() {
    return getClonedObject(this.appProjectThemes) || [];
  }

  getThemeVersionConflicts() {
    let conflicts: any = [],
      themes = this.getProjectThemes();
    if(this.projectService.getProjectPlatformType() !== 'NATIVE_MOBILE' && this.projectService.getProjectPlatformType() !== 'WEB' && isEmpty(this.appThemeNamePropertiesMap)){
      return  conflicts;
    }

    return this.studioService.getSelectedOrganization().then((res) => {
      this.selectedOrganization = res;
      themes.forEach((theme: any) => {
        let latestVersion = theme.workspaceVersion === null ? this.appThemeNamePropertiesMap[theme.name]?.version : theme.workspaceVersion;

        if ( latestVersion && (theme.version !== latestVersion || theme.artifactStatus === 'DEV')) {
          theme.versionMessage = this.constructThemeConflictMessage(theme, latestVersion);
          theme.latestVersion = latestVersion;
          conflicts.push(theme);
        }
      });
      return conflicts;
    });
  }

  constructThemeConflictMessage(theme: any, latestVersion:any) {
    let msg,
      actionsBtnMsg = 'Update',
        currentVer = theme.version,
        latestVer = latestVersion,
      currentVerParts = theme.version?.split('.'),
        latestVerParts = latestVersion?.split('.');

    currentVer = currentVerParts?.length == 2 ? currentVer + '.0' :  currentVer;
    latestVer = latestVerParts?.length == 2 ? latestVer + '.0' : latestVer;

    if (semver.valid(latestVer) && semver.valid(currentVer)) {
      if (semver.compare(currentVer, latestVer)  === -1) {
        msg = this.getLocalizedMessage('MESSAGE_THEME_VERSION_MISMATCH_OLDER', theme.name, theme.version, latestVersion, actionsBtnMsg);
      } else {
        actionsBtnMsg = 'Revert';
        msg = this.getLocalizedMessage('MESSAGE_THEME_VERSION_MISMATCH_FUTURE', theme.name, theme.version, latestVersion, actionsBtnMsg);
      }
    } else {
      if (latestVer !== currentVer) {
        msg = this.getLocalizedMessage('MESSAGE_THEME_VERSION_MISMATCH_CONFLICT', theme.name, latestVersion, actionsBtnMsg);
      }
    }
    if (theme.artifactStatus === 'DEV') {
      if (semver.compare(currentVer, latestVer)  === -1) {
        actionsBtnMsg = 'Update';
        msg = this.getLocalizedMessage('MESSAGE_THEME_VERSION_MISMATCH_DEVELOPMENT', theme.name, actionsBtnMsg);
      } else {
          actionsBtnMsg = 'Revert';
         msg = this.getLocalizedMessage('MESSAGE_THEME_VERSION_MISMATCH_DEVELOPMENT', theme.name, actionsBtnMsg);
      }
    }
    theme.actionsBtnMsg = actionsBtnMsg;
    return  msg;
  }

  getLocalizedMessage(message: any, ...args: any) {
    let key = this.locale[message];
    return replace(key, args);
  }
}
